import Axios from "axios";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { decrypt, encrypt, getDomain } from "../../Utilities/utils";
import { CREATE_QUOTE_ENDPOINT, KYC_API_ENDPOINT } from "../apiConfig";
import {
  CREATE_QUOTE_PAGE_ROUTE,
  PAYMENT_OPTIONS_PAGE_ROUTE,
  QUICK_QUOTE_ROUTE,
} from "../routes";
import { POLICY_STATUS } from "../PaymentOptionsPage/usePaymentOptions.js";
import Swal from "sweetalert2";

const useProposalCreation = () => {
  const [isLoading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [kycVerificationStatus, setKycVerificationStatus] = useState(null);
  const [formikValuesId, setFormikValuesId] = useState(null);
  const [createQuoteProposalStatus, setCreateQuoteProposalStatus] =
    useState(null);
  const { state } = useLocation();
  const values = state && state.values; // values can be null if the user comes from portal

  const [kycResponse, setKYCResponse] = useState(null);
  const [createQuoteResponse, setCreateQuoteResponse] = useState(null);

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const transferDataId = searchParams?.get("id"); // Transfer data id
  let decryptedTransferDataId = null;
  if (transferDataId) {
    decryptedTransferDataId = decrypt(decodeURIComponent(transferDataId));
  }

  const KYCStatus = {
    VERIFIED: "verified", // KYC Verified
    FAILED: "failed", // KYC Failed/Not Verified
    ERROR: "error", // Async Error
  };
  const CreateQuoteProposalStatus = {
    SUCCESS: "success", // Successfully created the quote proposal
    ALREADY_GENERATED: "already_generated", // Already generated the quote proposal
    FAILED: "failed", // Failed to create the quote proposal (Error got from the insurance company)
    ERROR: "error", // Async Error
  };

  useEffect(() => {
    if (decryptedTransferDataId) {
      kycProcess();
    } else {
      // if the user comes from the portal
      const createQuoteId = searchParams?.get("createQuoteId");
      setLoading(true);
      setStep(1);
      setKycVerificationStatus(KYCStatus.VERIFIED);
      if (createQuoteId) createProposal(createQuoteId);
    }
  }, [searchParams, decryptedTransferDataId]);

  const domain = getDomain();

  const kycProcess = async () => {
    setLoading(true);
    try {
      let response = await Axios.post(
        `${domain}${KYC_API_ENDPOINT}/${decryptedTransferDataId}/${state?.formikValuesId}/`,
        JSON.stringify(values)
      );
      console.log("kyc response", response);
      response = response?.data?.KycResponse;
      setKYCResponse(response);
      if (response?.kyc_verified === "true") {
        setStep(1);
        setKycVerificationStatus(KYCStatus.VERIFIED);
        createProposal(response?.create_quote_id);
      } else if (response?.kyc_verified === "false") {
        setFormikValuesId(response?.create_quote_id);
        setKycVerificationStatus(KYCStatus.FAILED);
        setLoading(false);
      }
      // If proposal already generated
      else if (
        response?.policy_status === POLICY_STATUS.CREATE_QUOTE_SUBMISSION
      ) {
        console.log("response?.policy_status: ", response?.policy_status);
        setKycVerificationStatus(KYCStatus.VERIFIED);
        setCreateQuoteProposalStatus(
          CreateQuoteProposalStatus.ALREADY_GENERATED
        );
        Swal.fire({
          title: "Proposal already generated",
          text: "Proceed to payment or go back to quick quote.",
          icon: "warning",
          confirmButtonColor: "#0d409b",
          cancelButtonColor: "#d33",
          confirmButtonText: "Proceed to Payment",
          cancelButtonText: "Go to Quick Quote",
          showCancelButton: true,

          reverseButtons: true,
        }).then((result) => {
          if (result.isConfirmed) {
            goToPaymentPage();
          } else if (result.dismiss === Swal.DismissReason.cancel) {
            navigate(QUICK_QUOTE_ROUTE);
          } else {
            goToPaymentPage();
          }
        });
        setLoading(false);
      } else {
        setFormikValuesId(response?.create_quote_id);
        setKycVerificationStatus(KYCStatus.ERROR);
        setLoading(false);
      }
    } catch (error) {
      const response = error?.response?.data?.KycResponse;
      setKYCResponse(response);
      console.log("response: ", response);
      setFormikValuesId(response?.create_quote_id);
      setKycVerificationStatus(KYCStatus.ERROR);
      setLoading(false);
    }
  };

  const createProposal = async (id) => {
    try {
      const response = await Axios.post(
        `${domain}${CREATE_QUOTE_ENDPOINT}/${id}/`
      );
      console.log("create quote response", response);
      setCreateQuoteProposalStatus(CreateQuoteProposalStatus.SUCCESS);
      setCreateQuoteResponse(response?.data);
      setStep(2);
    } catch (error) {
      const response = error.response.data;
      console.log("response: ", response);
      setFormikValuesId(response?.create_quote_id);
      setCreateQuoteResponse(response);
      setCreateQuoteProposalStatus(CreateQuoteProposalStatus.FAILED);
      setLoading(false);
      alert(response?.error?.message);
    }
  };

  const goToPortal = () => {
    const portalUrl = kycResponse.portal_url;
    window.location.href = portalUrl;
  };

  const goBackToCreateQuotePage = async () => {
    setLoading(true);
    try {
      // If kyc failed then create quote response will be null
      const productRef =
        createQuoteResponse?.product_ref ?? kycResponse?.product_ref;
      const encryptedProductRef =
        productRef !== null ? encrypt(productRef) : null;
      const transferDataId =
        createQuoteResponse?.transfer_data_id ?? kycResponse?.transfer_data_id;
      const encryptedTransferDataId =
        transferDataId !== null ? encrypt(transferDataId) : null;

      let path = `${CREATE_QUOTE_PAGE_ROUTE}/${encodeURIComponent(
        encryptedProductRef
      )}/${encodeURIComponent(encryptedTransferDataId)}`;

      navigate(path, { state: { formikValuesId } });
    } catch (error) {
      console.log("error: ", error);
      alert("Something went wrong...");
    }
  };

  const goToPaymentPage = () => {
    console.log("goToPaymentPage: ", goToPaymentPage);
    const newTransferDataId =
      transferDataId || createQuoteResponse?.transfer_data_id;
    navigate(
      `${PAYMENT_OPTIONS_PAGE_ROUTE}/${encodeURIComponent(newTransferDataId)}`
    );
  };

  const retryCreateProposal = () => {
    setLoading(true);
    setStep(1);
    setKycVerificationStatus(KYCStatus.VERIFIED);
    if (kycResponse !== null) {
      createProposal(kycResponse.id);
    } else {
      const createQuoteId = searchParams?.get("createQuoteId");
      createProposal(createQuoteId);
    }
  };

  const progress = (step / 2) * 100;

  return {
    progress,
    isLoading,
    kycVerificationStatus,
    createQuoteProposalStatus,
    kycProcess,
    createProposal,
    goToPortal,
    goBackToCreateQuotePage,
    step,
    KYCStatus,
    CreateQuoteProposalStatus,
    goToPaymentPage,
    retryCreateProposal,
    kycErrorMessage: kycResponse?.error_message,
    proposalCreationErrorMessage: createQuoteResponse?.error?.message,
  };
};

export default useProposalCreation;
